import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import JsonEditor from 'components/editors/JsonEditor';
import { useState } from 'react';

interface RawJsonPanelProps<T> {
  /** Initial serialized JSON; the panel owns its draft from here while open. */
  value: string;
  /** Parse the draft into structured data; throw on invalid input. */
  parse: (text: string) => T;
  /** Called with parsed data on every valid edit. */
  onParsed: (parsed: T) => void;
  /** Called whenever the error state flips, so the parent can gate "Run". */
  onError: (hasError: boolean) => void;
  minRows?: number;
  ariaLabel?: string;
}

/**
 * Raw-JSON escape hatch. Structured state stays the source of truth: this only pushes parsed
 * results up on valid edits, and reports errors instead of corrupting the structured model.
 * The {@link JsonEditor} surface adds syntax highlighting and inline gutter error markers.
 */
const RawJsonPanel = <T,>({ value, parse, onParsed, onError, minRows = 8, ariaLabel }: RawJsonPanelProps<T>) => {
  const [text, setText] = useState(value);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (next: string) => {
    setText(next);
    try {
      const parsed = parse(next);
      setError(null);
      onError(false);
      onParsed(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'JSON inválido');
      onError(true);
    }
  };

  return (
    <Box>
      <JsonEditor
        value={text}
        onChange={handleChange}
        error={Boolean(error)}
        minHeight={Math.round(minRows * 20 + 16)}
        maxHeight={480}
        ariaLabel={ariaLabel}
      />
      {error && (
        <Typography sx={{ mt: 0.5, fontSize: 11.5, color: '#E7233B', fontFamily: 'Jost, sans-serif' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default RawJsonPanel;
